import { usePolling, useStaticCallback } from '@sparemin/blockhead';
import { useCallback, useEffect } from 'react';
import { MediaSegment } from 'redux/modules/entities';
import { skipGaps } from 'utils/embed/media-segment';
import { millisToSec, secToMillis } from 'utils/time';
import { VideoEventEmitter } from './types';

export type UseTimeupdateOptions = {
  emitter: VideoEventEmitter;
  onTimeupdate?: () => void;
  segments?: MediaSegment[];
  onSkipGap?: () => void;
  timeupdateIntervalMillis?: number;
  videoRef: React.RefObject<HTMLVideoElement>;
};

/**
 * Handles skipping deletions during playback, as well as firing onTimeupdate
 */
export function useTimeupdate({
  emitter,
  onSkipGap,
  onTimeupdate,
  segments,
  timeupdateIntervalMillis,
  videoRef,
}: UseTimeupdateOptions) {
  const handleTimeupdate = useCallback(() => {
    const currentTime = secToMillis(videoRef.current?.currentTime);
    const adjustedTime = skipGaps(currentTime, segments);
    if (adjustedTime !== currentTime) {
      onSkipGap?.();
      videoRef.current.currentTime = millisToSec(adjustedTime);
    }
    onTimeupdate?.();
    emitter.emit('timeupdate');
  }, [videoRef, segments, onTimeupdate, emitter, onSkipGap]);

  const [startTimeupdate, stopTimeupdate] = usePolling(handleTimeupdate, {
    intervalMillis: timeupdateIntervalMillis,
  });

  const handlePlay = useStaticCallback(() => {
    startTimeupdate();
  });

  const handlePause = useStaticCallback(() => {
    stopTimeupdate();
  });

  const handleSeeked = useStaticCallback(() => {
    // TODO SPAR-26685 - test this.  could cause issues during video playback.
    // if so, hook into seeked event instead
    onTimeupdate?.();
    emitter.emit('timeupdate');
  });

  useEffect(() => {
    if (videoRef.current) {
      const video = videoRef.current;

      video.addEventListener('play', handlePlay);
      video.addEventListener('pause', handlePause);
      video.addEventListener('seeked', handleSeeked);

      return () => {
        video.removeEventListener('play', handlePlay);
        video.removeEventListener('pause', handlePause);
        video.removeEventListener('seeked', handleSeeked);
      };
    }
    return undefined;
  }, [handlePlay, handlePause, handleSeeked, videoRef]);
}
